import React from "react";
import {
  GraduationCap,
  Wrench,
  School,
  BookOpen,
  Users,
  Coins,
  HeartPulse,
  MessageSquare,
  Sprout,
  Home as HomeIcon,
  Leaf,
  AlertTriangle,
  Globe,
} from "lucide-react";

const Stitch = ({ className = "", color = "#2E7D32" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const objectives = [
  {
    icon: GraduationCap,
    title: "Skill Development",
    description: "Promoting skill development and livelihood generation across multiple key sectors.",
  },
  {
    icon: Wrench,
    title: "Vocational Training",
    description: "Undertaking vocational education and training aligned with market requirements.",
  },
  {
    icon: School,
    title: "Educational Institutions",
    description: "Establishing educational institutions to make quality learning accessible.",
  },
  {
    icon: BookOpen,
    title: "Scholarships & Support",
    description: "Providing scholarships, financial aid, and educational support to deserving students.",
  },
  {
    icon: Users,
    title: "Women's Empowerment",
    description: "Encouraging self-reliance and leadership through Self Help Groups (SHGs).",
  },
  {
    icon: Coins,
    title: "Entrepreneurship & Microfinance",
    description: "Supporting microfinance initiatives and entrepreneurship for livelihood generation.",
  },
  {
    icon: Sprout,
    title: "Organic Agriculture",
    description: "Promoting agricultural development, training, and organic farming techniques.",
  },
  {
    icon: HomeIcon,
    title: "Social Welfare Facilities",
    description: "Establishing and supporting hostels, orphanages, and old age homes.",
  },
  {
    icon: HeartPulse,
    title: "Healthcare Initiatives",
    description: "Providing healthcare services, support, and medical aid to vulnerable groups.",
  },
  {
    icon: MessageSquare,
    title: "Health & Social Awareness",
    description: "Creating awareness about health, hygiene, and key social issues.",
  },
  {
    icon: Leaf,
    title: "Environmental Sustainability",
    description: "Promoting sustainability, eco-friendly habits, and environmental conservation.",
  },
  {
    icon: AlertTriangle,
    title: "Disaster Relief",
    description: "Providing disaster relief, rehabilitation, and humanitarian support during crises.",
  },
  {
    icon: Globe,
    title: "Community Development",
    description: "Facilitating inclusive and holistic community development programmes.",
    isWide: true,
  },
];

const FocusAreas = () => {
  return (
    <section id="objectives" className="py-24 bg-[#FBF9F3] scroll-mt-20">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="mbf-mono text-[12px] tracking-[0.18em] uppercase text-[#2E7D32]">
            Trust Objectives
          </span>
          <h2 className="mbf-display mt-4 text-4xl font-semibold text-[#0A355C] leading-tight tracking-tight">
            Our Core Objectives &amp; Focus Areas
          </h2>
          <Stitch className="w-24 h-2 mx-auto mt-5" color="#2E7D32" />
          <p className="mbf-body mt-6 text-[#514a3d] leading-8">
            As outlined in our Trust Deed, MBFSDL operates across thirteen
            clauses of social welfare, empowerment, and sustainable
            development — numbered here exactly as they read in the deed.
          </p>
        </div>

        {/* Objectives Grid — index-card / stamped clause style */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {objectives.map((item, index) => {
            const Icon = item.icon;
            const num = String(index + 1).padStart(2, "0");

            /* Clause 13 closes the deed — rendered as a full-width
               indigo banner instead of another index card. */
            if (item.isWide) {
              return (
                <div
                  key={index}
                  className="md:col-span-2 lg:col-span-3 relative bg-[#082440] text-white p-9 md:p-10 overflow-hidden"
                >
                  <div
                    className="absolute inset-0 opacity-[0.06] pointer-events-none"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 30px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 30px)",
                    }}
                  />
                  <div className="relative flex flex-col md:flex-row md:items-center gap-6">
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="w-14 h-14 rounded-full border-2 border-[#E0A526] flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#E0A526]" />
                      </div>
                      <span className="mbf-mono text-[13px] text-[#E0A526]/80">
                        Clause {num}
                      </span>
                    </div>
                    <Stitch className="hidden md:block w-16 h-2 shrink-0" color="#E0A526" />
                    <div>
                      <h3 className="mbf-display text-2xl font-semibold">
                        {item.title}
                      </h3>
                      <p className="mbf-body mt-2 text-blue-100/75 leading-relaxed text-[15px] max-w-2xl">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={index}
                className="group relative bg-white border border-[#0A355C]/10 p-7 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#0A355C]/5 transition-all duration-300"
              >
                {/* stitched top edge */}
                <Stitch
                  className="absolute -top-[1px] left-6 right-6 h-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  color="#2E7D32"
                />

                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-full border-2 border-[#0A355C] flex items-center justify-center transition-all duration-300 group-hover:bg-[#0A355C]">
                    <Icon className="w-5 h-5 text-[#0A355C] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <span className="mbf-mono text-[12px] text-[#2E7D32]/60 pt-1">
                    {num}
                  </span>
                </div>

                <h3 className="mbf-display text-xl font-semibold text-[#0A355C]">
                  {item.title}
                </h3>

                <p className="mbf-body mt-3 text-[#475569] leading-relaxed text-[14px]">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FocusAreas;